import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

interface StatItem {
  value: string;
  label: string;
  suffix?: string;
}

interface StatsCounterProps {
  stats: StatItem[];
}

const StatsCounter = ({ stats }: StatsCounterProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div ref={ref} className="grid grid-cols-2 md:grid-cols-4 gap-8">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          className="text-center"
        >
          <div className="text-4xl md:text-5xl font-bold gradient-brand-text mb-2">
            {isInView ? (
              <CountUpAnimation value={stat.value} suffix={stat.suffix} />
            ) : (
              "0"
            )}
          </div>
          <div className="text-sm text-muted-foreground">{stat.label}</div>
        </motion.div>
      ))}
    </div>
  );
};

const CountUpAnimation = ({ value, suffix = "" }: { value: string; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
  const isPercentage = value.includes('%');
  const isInfinity = value === '∞' || value.includes('∞');

  useEffect(() => {
    if (isInfinity) return;
    
    let startTime: number;
    const duration = 2000;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(progress * numericValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [numericValue, isInfinity]);

  if (isInfinity) return <>{value}</>;
  
  return (
    <>
      {Math.round(count)}{isPercentage ? '%' : ''}{suffix}
    </>
  );
};

export default StatsCounter;
